import { EditorState } from '@tiptap/pm/state';

// Utils
import { getLinkifyMatchesFromText } from '../../../utils/getLinksFromText';
import isLinkAnEmail from '../hyperlink/isLinkAnEmail';

// Types
import { TiptapMarkType, TiptapNodeType } from '../../tiptapTypes';
import { UniversalCardConverterCommand } from './universalCardTypes';
import { ElementType } from '../../../elements/elementTypes';

/**
 * Gets the text of the editor if it's entirely a link with no formatting.
 */
const getTextStartLinkMatch = (state: EditorState): string | null => {
    // Document must have exactly one child
    if (state.doc.content.childCount !== 1) return null;

    const firstNode = state.doc.content.firstChild;

    // Must be a paragraph node
    if (!firstNode || firstNode.type.name !== TiptapNodeType.paragraph) return null;

    // Paragraph must contain exactly one text node
    if (firstNode.content.childCount !== 1) return null;

    const textNode = firstNode.content.firstChild;
    if (!textNode || textNode.type.name !== TiptapNodeType.text) return null;

    // Text must have no inline code marks
    if (textNode.marks.some((mark) => mark.type.name === TiptapMarkType.code)) return null;

    const text = state.doc.textContent;
    const linkMatches = getLinkifyMatchesFromText(text);

    // Must have exactly one link match that spans the entire text
    if (linkMatches.length !== 1) return null;

    const match = linkMatches[0];
    if (match.index !== 0 || match.text !== text) return null;

    return match.text;
};

/**
 * Determines if the current editor text is only a link.
 */
export const isOnlyLinkText = (state: EditorState): boolean => {
    const linkMatch = getTextStartLinkMatch(state);
    return linkMatch !== null && !isLinkAnEmail(linkMatch);
};

/**
 * Returns an action to dispatch if link conversion is possible.
 */
export const attemptLinkConversion: UniversalCardConverterCommand = ({ state, updateCurrentlyEditingElementType }) => {
    if (!isOnlyLinkText(state)) return null;

    return updateCurrentlyEditingElementType({
        elementType: ElementType.LINK_TYPE,
        data: { url: state.doc.textContent },
    });
};
